import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ALL_POLICY_CATEGORIES } from '@shared/guidedPlanningPreferences';
import { useSessionMutations } from '@/features/guided-financial-planning/context';
import { guidedPlanningQuoteKeys } from '@/features/guided-financial-planning/api';

/**
 * Compares two category arrays for equality (order-independent).
 */
function areCategoriesEqual(
  a: GuidedPlanningPolicyCategory[],
  b: GuidedPlanningPolicyCategory[],
): boolean {
  return a.length === b.length && a.every(cat => b.includes(cat));
}

/**
 * Hook for managing policy category selection on the quotes selection step.
 * Directly updates selectedPolicyCategories in the session.
 * Supports edit mode where changes are made locally and saved on demand.
 */
export function useQuoteFilters(session: GuidedPlanningSessionResponse | null | undefined) {
  const { updateStepData, isSaving } = useSessionMutations();
  const queryClient = useQueryClient();

  const [isEditing, setIsEditing] = useState(false);
  const [draftCategories, setDraftCategories] = useState<GuidedPlanningPolicyCategory[] | null>(
    null,
  );

  // Current saved categories from session
  const savedCategories: GuidedPlanningPolicyCategory[] = useMemo(() => {
    const categories = session?.stepData?.selectedPolicyCategories;
    return categories && categories.length > 0 ? categories : [...ALL_POLICY_CATEGORIES];
  }, [session?.stepData?.selectedPolicyCategories]);

  // Current categories to display (draft if editing, saved otherwise)
  const categories = isEditing && draftCategories ? draftCategories : savedCategories;

  // Check if draft has unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    if (!isEditing || !draftCategories) return false;
    return !areCategoriesEqual(draftCategories, savedCategories);
  }, [isEditing, draftCategories, savedCategories]);

  const startEditing = useCallback(() => {
    setDraftCategories([...savedCategories]);
    setIsEditing(true);
  }, [savedCategories]);

  const cancelEditing = useCallback(() => {
    setDraftCategories(null);
    setIsEditing(false);
  }, []);

  const invalidateQuotes = useCallback(() => {
    if (session?.clientId && session?.id) {
      queryClient.invalidateQueries({
        queryKey: guidedPlanningQuoteKeys.list({
          clientId: session.clientId,
          sessionId: session.id,
        }),
      });
    }
  }, [queryClient, session?.clientId, session?.id]);

  const saveFilters = useCallback(async () => {
    if (!draftCategories) return;
    await updateStepData({ selectedPolicyCategories: draftCategories });
    invalidateQuotes();
    setDraftCategories(null);
    setIsEditing(false);
  }, [draftCategories, updateStepData, invalidateQuotes]);

  const toggleCategory = useCallback(
    (category: GuidedPlanningPolicyCategory) => {
      if (!isEditing || !draftCategories) return;

      const isSelected = draftCategories.includes(category);

      // Don't allow deselecting if it's the last one
      if (isSelected && draftCategories.length === 1) return;

      const newCategories = isSelected
        ? draftCategories.filter(cat => cat !== category)
        : [...draftCategories, category];

      setDraftCategories(newCategories);
    },
    [isEditing, draftCategories],
  );

  return {
    categories,
    isEditing,
    hasUnsavedChanges,
    isSaving,
    startEditing,
    cancelEditing,
    saveFilters,
    toggleCategory,
  };
}
